// src/pages/AdminSettingsPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Shield,
  Bell,
  Lock,
  User,
  AlertTriangle,
  Users,
  MapPin,
} from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../context/AuthContext';
import { adminSettingsService } from '../services/adminServices';
import AddAdminModal from '../components/AddAdminModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import RegionManagementPanel from '../components/settings/RegionManagementPanel';

function AdminSettingsPage() {
  const [isAddAdminModalOpen, setIsAddAdminModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [adminActionError, setAdminActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [filteredAdmins, setFilteredAdmins] = useState([]);

  const [securityLogs, setSecurityLogs] = useState([]);
  const [accountForm, setAccountForm] = useState({
    name: '',
    email: '',
    title: ''
  });

  const { user, hasPermission } = useAuth();
  const canManageAdmins = hasPermission('manage_admins');
  const canManageRegions = hasPermission('manage_regions');
  

  // Set initial tab based on permissions
  const getInitialTab = () => {
    if (canManageAdmins) return 'roles';
    if (canManageRegions) return 'regions'; // Add regions as a fallback if they have that perm
    return 'account'; // Default for users without specific management perms
  }

  const [activeTab, setActiveTab] = useState(getInitialTab());

  console.log('[AdminSettingsPage] Rendering with permissions:', {
    canManageRegions,
    activeTab
  });

  // Fetch admins on mount if user has permission
  useEffect(() => {
    if (canManageAdmins && activeTab === 'roles') {
      fetchAdmins();
    }
  }, [canManageAdmins, activeTab]);

  // Fetch security logs when security tab is active
  useEffect(() => {
    if (activeTab === 'security') {
      fetchSecurityLogs();
    }
  }, [activeTab]);

  // Set initial filtered admins when admins are fetched
  useEffect(() => {
    setFilteredAdmins(admins);
  }, [admins]);

  // Initialize account form with user data
  useEffect(() => {
    if (user) {
      setAccountForm({
        name: user.name || '',
        email: user.email || '',
        title: user.title || ''
      });
    }
  }, [user]);


  // Search effect using debounced term
  useEffect(() => {
    if (!admins) return;  
    if (!debouncedSearchTerm.trim()) {
      setFilteredAdmins(admins);
      return;
    }
  
    const searchLower = debouncedSearchTerm.toLowerCase();
    const filtered = admins.filter(admin => 
      admin.name?.toLowerCase().includes(searchLower) ||
      admin.email?.toLowerCase().includes(searchLower) ||
      admin.title?.toLowerCase().includes(searchLower) ||
      admin.role?.toLowerCase().includes(searchLower)
    );
  
    console.log('[AdminSettingsPage - Search Results]', {
      searchTerm: searchLower,
      results: filtered.length,
      filtered
    });
  
    setFilteredAdmins(filtered);
  }, [debouncedSearchTerm, admins]);
  
  
  

  const fetchAdmins = async () => {
    try {
      setIsLoading(true);
      const { admins } = await adminSettingsService.getAdmins();
      console.log('[AdminSettingsPage - Fetched Admins]', admins); // Add this
      setAdmins(admins);
    } catch (error) {
      console.error('[AdminSettingsPage - Fetch Admins Error]:', error);
      setAdminActionError('Failed to fetch admin list');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSecurityLogs = async () => {
    try {
      setIsLoading(true);
      const { logs } = await adminSettingsService.getSecurityLogs();
      setSecurityLogs(logs);
    } catch (error) {
      console.error('[AdminSettingsPage - Fetch Security Logs Error]:', error);
      setAdminActionError('Failed to fetch security logs');
    } finally {
      setIsLoading(false);
    }
  };

  const recentlyActiveAdmins = useMemo(() => {
    if (!admins) return [];
    
    return [...admins]
      .sort((a, b) => new Date(b.lastLogin) - new Date(a.lastLogin))
      .filter(admin => admin.status === 'active')
      .slice(0, 4); // Show top 4 most recently active
  }, [admins]);

  const handleAddAdmin = async (formData) => {
    console.log('[AdminSettingsPage - Add Admin]', { 
      formData: { ...formData, password: '***' }
    });

    setIsLoading(true);
    setAdminActionError('');
    setActionSuccess('');

    try {
      await adminSettingsService.createAdmin(formData);
      setActionSuccess('Admin created successfully');
      setIsAddAdminModalOpen(false);
      fetchAdmins(); // Refresh admin list
    } catch (error) {
      console.error('[AdminSettingsPage - Add Admin Error]:', error);
      setAdminActionError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (passwordData) => {
    console.log('[AdminSettingsPage - Change Password]');

    setIsLoading(true);
    setAdminActionError('');
    setActionSuccess('');

    try {
      await adminSettingsService.changePassword(passwordData);
      setActionSuccess('Password changed successfully');
      setIsChangePasswordModalOpen(false);
    } catch (error) {
      console.error('[AdminSettingsPage - Password Change Error]:', error);
      setAdminActionError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountUpdate = async () => {
    console.log('[AdminSettingsPage - Update Account]', accountForm);

    setIsLoading(true);
    setAdminActionError('');
    setActionSuccess('');

    try {
      setActionSuccess('Account updated successfully');
    } catch (error) {
      console.error('[AdminSettingsPage - Update Account Error]:', error);
      setAdminActionError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAdminStatus = async (adminId, newStatus) => {
    console.log('[AdminSettingsPage - Toggle Admin Status]', { 
      adminId, 
      newStatus 
    });

    setIsLoading(true);
    setAdminActionError('');
    setActionSuccess('');

    try {
      await adminSettingsService.toggleAdminStatus(adminId, newStatus);
      setActionSuccess('Admin status updated successfully');
      fetchAdmins(); // Refresh admin list
    } catch (error) {
      console.error('[AdminSettingsPage - Toggle Status Error]:', error);
      setAdminActionError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear messages when changing tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setAdminActionError('');
    setActionSuccess('');
  };

  return (
    <div className="space-y-6">
      {/* Add Admin Modal */}
      <AddAdminModal
        isOpen={isAddAdminModalOpen}
        onClose={() => setIsAddAdminModalOpen(false)}
        onSubmit={handleAddAdmin}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onSubmit={handlePasswordChange}
      />

       {/* Page Header */}
       <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Admin Settings</h1>
          <p className="mt-1 text-sm text-gray-500">Manage your admin preferences and system settings</p>
        </div>
      </div>

      {/* Success Message */}
      {actionSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">
          {actionSuccess}
        </div>
      )}

      {/* Error Message */}
      {adminActionError && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {adminActionError}
        </div>
      )}

      {/* Settings Layout */}
      <div className="lg:flex lg:gap-6">
        {/* Settings Navigation */}
        <div className="w-full lg:w-64 mb-6 lg:mb-0 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-shrink-0">
          <nav className="flex flex-col p-4 space-y-1">
            {/* Roles & Permissions Tab */}
            {canManageAdmins && ( // Only show if user can manage admins
              <button
                onClick={() => handleTabChange('roles')}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium w-full text-left ${ // Added w-full text-left
                  activeTab === 'roles'
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Shield className="h-5 w-5 flex-shrink-0" />
                <span>Roles & Permissions</span>
              </button>
            )}

            {/* --- Region Management Tab --- */}
            {canManageRegions && ( 
                 <button
                    onClick={() => handleTabChange('regions')}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium w-full text-left ${
                    activeTab === 'regions'
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                    <MapPin className="h-5 w-5 flex-shrink-0" />
                    <span>Region Management</span>
                </button>
            )}


            {/* Notifications Tab */}
            <button
              onClick={() => handleTabChange('notifications')}
              className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium w-full text-left ${ // Added w-full text-left
                activeTab === 'notifications'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Bell className="h-5 w-5 flex-shrink-0" />
                <span>Notifications</span>
              </div>
              <span className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Beta</span> {/* Adjusted padding */}
            </button>

            {/* Security Tab */}
            <button
              onClick={() => handleTabChange('security')}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium w-full text-left ${ // Added w-full text-left
                activeTab === 'security'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Lock className="h-5 w-5 flex-shrink-0" />
              <span>Security</span>
            </button>

            {/* Admin Account Tab */}
            <button
              onClick={() => handleTabChange('account')}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium w-full text-left ${ // Added w-full text-left
                activeTab === 'account'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <User className="h-5 w-5 flex-shrink-0" />
              <span>My Account</span>
            </button>
          </nav>
        </div>


        {/* Settings Content */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200">

        {activeTab === 'roles' && (
          <div className="p-6 space-y-6">
            {/* Header with Add Admin button */}
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Roles & Permissions</h2>
              {canManageAdmins && (
                <button 
                  onClick={() => setIsAddAdminModalOpen(true)}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Add New Admin
                </button>
              )}
            </div>

            {/* Recently Active Admins */}
            <div className="border-b pb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Recently Active Admins</h3>
              <div className="space-y-4">
                {recentlyActiveAdmins.map(admin => (
                  <div key={admin.id} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{admin.name}</p>
                        <p className="text-sm text-gray-500">{admin.email}</p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {admin.lastLogin ? (
                        `Last active ${new Date(admin.lastLogin).toLocaleDateString()}`
                      ) : 'Never logged in'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Admin Search */}
            <div className="w-full">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, email, title, or role..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Users className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            {/* Full Admin list */}
            {isLoading ? (
              <div className="text-center py-6">
                <p className="text-gray-500">Loading admins...</p>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium text-gray-900">All Admins</h3>
                  <p className="text-sm text-gray-500">
                    {debouncedSearchTerm ? `${filteredAdmins.length} results` : `${admins.length} admins`}
                  </p>
                </div>
                <div className="space-y-4">
                  {filteredAdmins.length === 0 && debouncedSearchTerm ? (
                    <div className="text-center py-6">
                      <p className="text-gray-500">No admins found matching "{debouncedSearchTerm}"</p>
                    </div>
                  ) : (
                    filteredAdmins.map(admin => (
                      <div key={admin._id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{admin.name}</h4>
                            <p className="text-sm text-gray-500">{admin.email}</p>
                            {admin.title && (
                              <p className="text-sm text-gray-500">{admin.title}</p>
                            )}
                            <p className="text-sm text-blue-600 mt-1">{admin.role}</p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              admin.status === 'active' 
                                ? 'bg-green-100 text-green-600'
                                : 'bg-red-100 text-red-600'
                            }`}>
                              {admin.status}
                            </span>
                            {canManageAdmins && admin.id !== user?.id && (
                              <button 
                                className="text-blue-600 text-sm hover:text-blue-700"
                                onClick={() => {
                                  console.log('[AdminSettingsPage - Toggle Button Click]', {
                                    admin: admin,
                                    adminId: admin.id,
                                    _id: admin._id, // Check if we have _id instead of id
                                    status: admin.status
                                  });
                                  handleToggleAdminStatus(
                                    admin._id,
                                    admin.status === 'active' ? 'disabled' : 'active'
                                  );
                                }}
                                disabled={isLoading}
                              >
                                {admin.status === 'active' ? 'Disable' : 'Enable'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* --- Region Management Content --- */}
        {activeTab === 'regions' && canManageRegions && (
            <RegionManagementPanel /> 
        )}

        {activeTab === 'security' && (
          <div className="p-6 space-y-6">
            {/* Security logs with real data */}
            {isLoading ? (
              <div className="text-center py-6">
                <p className="text-gray-500">Loading security logs...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {securityLogs.map((log, index) => (
                  <div key={index} className="flex items-start space-x-3 text-sm">
                    <AlertTriangle className={`h-5 w-5 ${
                      log.type === 'warning' ? 'text-yellow-500' : 'text-blue-500'
                    }`} />
                    <div>
                      <p className="text-gray-900">{log.message}</p>
                      <p className="text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="p-6">
            <div className="flex items-center space-x-2 mb-6">
              <h2 className="text-lg font-medium text-gray-900">Notification Settings</h2>
              <span className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Beta</span>
            </div>
            <div className="flex items-center justify-center h-48 border-2 border-dashed border-gray-200 rounded-lg">
              <p className="text-gray-500">Notification settings coming soon</p>
            </div>
          </div>
        )}

        {activeTab === 'account' && (
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-medium text-gray-900">Admin Account</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={accountForm.name}
                  onChange={(e) => setAccountForm(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={accountForm.email}
                  onChange={(e) => setAccountForm(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  value={accountForm.title}
                  onChange={(e) => setAccountForm(prev => ({
                    ...prev,
                    title: e.target.value
                  }))}
                  placeholder="e.g. Director of Operations"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <input
                  type="text"
                  value={user?.role || ''}
                  disabled
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50"
                />
              </div>

              <div className="pt-4">
                <button 
                  onClick={() => setIsChangePasswordModalOpen(true)}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Change Password
                </button>
              </div>

              <div className="pt-4">
                <button 
                  onClick={handleAccountUpdate}
                  disabled={isLoading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>
    </div>
  );
}

export default AdminSettingsPage;

